
import { LruCache } from '../lru_cache.js';
import { MarkupNode } from '../markup_node.js';

export const loadCache = LruCache.new();

// Accept header for HTML frame loads: advertises that the frame can consume a
// pre-parsed, serialized MarkupNode tree (parsed off the main thread by the
// service worker) as well as raw text/html.
export const HTML_FRAME_ACCEPT_HEADER = 'text/html, application/vnd.blognami.markup-node+json';

// Read a frame response into a serialized MarkupNode JSON string. The worker
// hands back the serialized type directly; a raw text/html fallback is parsed
// once here and serialized. Keeps loadCache uniform — every entry is a
// serialized string, so cache hits deserialize without re-parsing.
export async function readSerializedMarkup(response){
    const contentType = response.headers.get('Content-Type') || '';
    const text = await response.text();
    if(contentType.match(/application\/vnd\.blognami\.markup-node\+json/i)) return text;
    return new MarkupNode().appendHtml(text).serialize();
}

export function loadFrame({ confirm, target, method, url, placeholderUrl, values = this.values, requiresProofOfWork = false, skipPatch = false } = {}){
    if(confirm && !window.confirm(confirm)){
        return;
    }

    let frame;
    
    if(target == '_overlay'){
        const defaultPlaceholder = this.document.head.find('meta[name="blognami-overlay-default-placeholder"]')?.params.content;
        const defaultPlaceholderAttribute = defaultPlaceholder ? ` default-placeholder="${defaultPlaceholder.replace(/&/g, '&amp;').replace(/"/g, '&quot;')}"` : '';
        frame = this.document.descendants.find(node => node.is('body')).append(`<blognami-overlay load-on-init="false"${defaultPlaceholderAttribute}></blognami-overlay>`).pop();
        frame._parent = this;
        this._overlayChild = frame;
    } else if(target) {
        frame = getFrame.call(this, target);
    } else if(this.isFrame) {
        frame = this;
    }

    if(!frame) return;

    url = normalizeUrl(url || frame.url, this.frame.url);
    if(url.protocol != 'data:' && (url.host != frame.url.host || url.port != frame.url.port)){
        return;
    }

    if(placeholderUrl === undefined) placeholderUrl = frame.defaultPlaceholder;
    if(placeholderUrl) placeholderUrl = normalizeUrl(placeholderUrl, this.frame.url);

    if(method.match(/POST|PUT|PATCH/i)){
        const body = (async () => {
            const out = new FormData();
            for(const name in values) out.append(name, await values[name]);
            return out;
        })();
        frame.load(url, { method, body, placeholderUrl, requiresProofOfWork, skipPatch });
    } else {
        frame.load(url, { method, placeholderUrl, requiresProofOfWork, skipPatch });
    }    
}

export function getFrame(target){
    if(target == '_self') return this.frame;
    if(target == '_top') return this.document;
    if(target.match(/^_parent/)){
        const index = target.split(/_/).length - 1;
        return this.parents.filter(n => n.isFrame)[index];
    }
    return this.frame.descendants.find(n => n.isFrame && n.data.name == target);
}

export function normalizeUrl(url, referenceUrl = window.location){
    const matches = `${url}`.match(/^&(.*)$/);
    const out = matches ? new URL(referenceUrl) : new URL(url, referenceUrl);
    if(matches){
        out.search = `?${stringifyUrlSearch({
            ...parseUrlSearch(out.search),
            ...parseUrlSearch(`${matches[1]}`)
        })}`;
    }
    return out;
}

function parseUrlSearch(search){
    const out = {};
    search.replace(/^\?/, '').split('&').forEach(pair => {
        const [key, value] = pair.split('=');
        out[decodeURIComponent(key)] = decodeURIComponent(value);
    });
    return out;
}

function stringifyUrlSearch(search){
    return Object.keys(search).map(key => `${encodeURIComponent(key)}=${encodeURIComponent(search[key])}`).join('&');
}
